import { Box, Button, TextField, Typography, Container, Fade, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as Yup from 'yup'
import { useFormik } from "formik";
import { useState } from "react";
import { toast } from "react-toastify";
import { ArrowBack, Email } from "@mui/icons-material";
import { appColors } from "../../../constants/theme";
import {
    ForgotPasswordMainContainer,
    ForgotPasswordCard,
    ForgotPasswordHeader,
    ForgotPasswordBackButton
} from "../style";
import {
    useRequestPasswordResetMutation,
    useRequestEmployeePasswordResetMutation,
    useRequestAssistantPasswordResetMutation,
} from "../../../store/api/usersApiSlice";
import { isPasswordResetRateLimited } from "../../../utils/passwordResetErrors";

const ForgotPassword = ({ variant = "doctor" }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [sendEmailOnProgress, setSendEmailOnProgress] = useState(false);
    const [requestPasswordReset] = useRequestPasswordResetMutation();
    const [requestEmployeePasswordReset] = useRequestEmployeePasswordResetMutation();
    const [requestAssistantPasswordReset] = useRequestAssistantPasswordResetMutation();

    const validationSchema = Yup.object().shape({
        email: Yup.string()
            .email(t("invalid_email_address"))
            .max(320, t("maximum_letters_warning", { item: 320 }))
            .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, t("invalid_email_address"))
            .required(t("email_required")),
    });

    const requestReset = (email) => {
        const body = { email };
        if (variant === "employee") return requestEmployeePasswordReset(body).unwrap();
        if (variant === "assistant") return requestAssistantPasswordReset(body).unwrap();
        // Doktor/hasta: continueUrl zorunlu — aksi halde backend hasta URL'sini önceleyebilir
        body.continueUrl = `${window.location.origin}/reset-password`;
        return requestPasswordReset(body).unwrap();
    };

    const handleForgotPassword = async (values) => {
        try {
            setSendEmailOnProgress(true);
            await requestReset(values.email);
            toast.success(t("password_reset_email_sent_generic"));
        } catch (err) {
            if (isPasswordResetRateLimited(err)) {
                toast.error(t("password_reset_rate_limited"));
            } else {
                toast.success(t("password_reset_email_sent_generic"));
            }
        } finally {
            setSendEmailOnProgress(false);
        }
    }

    const {
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        touched,
        errors
    } = useFormik({
        initialValues: {
            email: ''
        },
        validationSchema,
        validateOnBlur: true,
        onSubmit: () => handleForgotPassword(values)
    });

    const inputFieldStyle = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: appColors.white,
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
                borderWidth: '2px',
            },
        },
        '& .MuiInputLabel-root': {
            color: theme.palette.text.secondary,
            '&.Mui-focused': {
                color: theme.palette.primary.main,
            },
        },
    };

    return (
        <ForgotPasswordMainContainer>
            <Container maxWidth="sm">
                <Fade in timeout={800}>
                    <ForgotPasswordCard>
                        <ForgotPasswordHeader>
                            <ForgotPasswordBackButton
                                onClick={() => window.history.back()}
                            >
                                <ArrowBack />
                            </ForgotPasswordBackButton>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 700,
                                    color: theme.palette.primary.main,
                                    textAlign: 'center',
                                    flex: 1,
                                }}
                            >
                                {t("forgot_password")}
                            </Typography>
                        </ForgotPasswordHeader>

                        <Typography
                            sx={{
                                color: theme.palette.text.secondary,
                                mb: 3,
                                textAlign: 'center',
                                fontSize: '16px',
                                lineHeight: 1.6
                            }}
                        >
                            {t('reset_password_text')}
                        </Typography>

                        <Box sx={{ mb: 3 }}>
                            <TextField
                                id="email"
                                name="email"
                                label={t('email')}
                                inputProps={{
                                    maxLength: 320
                                }}
                                variant="outlined"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={Boolean(errors.email) && touched.email}
                                helperText={touched.email && errors.email}
                                fullWidth
                                InputProps={{
                                    startAdornment: <Email sx={{ color: theme.palette.primary.main, mr: 1 }} />
                                }}
                                sx={inputFieldStyle}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleSubmit()
                                }}
                            />
                        </Box>

                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={sendEmailOnProgress}
                            fullWidth
                            sx={{
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                borderRadius: '12px',
                                height: '48px',
                                textTransform: 'none',
                                fontSize: '16px',
                                fontWeight: 600,

                                '&:hover': {
                                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,

                                    transform: 'translateY(-1px)',
                                },
                                '&:disabled': {
                                    background: theme.palette.grey[300],

                                    transform: 'none',
                                },
                                transition: 'all 0.3s ease'
                            }}
                        >
                            {sendEmailOnProgress ? t('sending') : t('send_reset_email')}
                        </Button>
                    </ForgotPasswordCard>
                </Fade>
            </Container>
        </ForgotPasswordMainContainer>
    )
}

export default ForgotPassword;
